/**
 * Handschutzplan-Modul · Baustein 9 (Feb 2026) — Mitarbeiter-Leseansicht.
 *
 * PLAKAT-FIRST · Async-Umstellung (28.02.2026, 2. Kurskorrektur):
 * Zwei-Schritt-Flow — ``prepare`` startet die Bild-Kette im Hintergrund,
 * der Client pollt ``plakat-status`` bis ``ready``/``error`` und lädt
 * dann das JPEG. Reuse-Fälle geben sofort ``ready`` zurück und
 * überspringen den Poll-Loop. Bei ``error`` (z. B. Tagesbudget)
 * zeigen wir eine differenzierte Meldung, Textansicht klappt auf.
 *
 * Kein separater „Generieren"-Button — der normale Klick auf den
 * Einsatzbereich löst prepare aus.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { KATEGORIE_LABELS } from "@/lib/handschutzplan";
import { useMonteurT } from "@/i18n/monteur";

// Ab dieser Wartedauer wird eine „Plakat wird erstellt"-Meldung eingeblendet.
// Reuse-Fälle sind meist in < 2 s durch (nur Render), damit erscheint der
// Hint hier nicht — nur bei echten Erst-Läufen.
const LOADING_HINT_DELAY_MS = 2000;

// Poll-Intervall für plakat-status. Aggressive 2,5 s → schnelle Reaktion
// bei ready/error, aber kein Server-Hammer bei langen Erst-Läufen.
const POLL_INTERVAL_MS = 2500;

// Sicherheitsobergrenze: nach ~3 min ist ein echter Fehler wahrscheinlicher
// als noch aktive Generierung; wir zeigen dann den Error-Zustand.
const POLL_HARD_TIMEOUT_MS = 180000;

function PlanTabelle({ zeilen, t }) {
  if (!zeilen?.length) return null;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <table className="w-full text-sm" data-testid="monteur-hsp-plan-tabelle">
        <thead>
          <tr className="bg-slate-900 text-white text-left">
            <th className="px-4 py-3 font-semibold w-[22%]">{t("hsp.thWas")}</th>
            <th className="px-4 py-3 font-semibold w-[26%]">{t("hsp.thWann")}</th>
            <th className="px-4 py-3 font-semibold w-[26%]">{t("hsp.thWomit")}</th>
            <th className="px-4 py-3 font-semibold w-[26%]">{t("hsp.thWie")}</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, idx) => (
            <tr
              key={idx}
              className="border-t border-slate-100 align-top"
              data-testid={`monteur-hsp-zeile-${idx}`}
            >
              <td className="px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  {KATEGORIE_LABELS[z.kategorie] || z.kategorie}
                </div>
                <div className="font-medium text-slate-900 mt-0.5">{z.was}</div>
              </td>
              <td className="px-4 py-3 text-slate-700">{z.wann}</td>
              <td className="px-4 py-3 text-slate-700">{z.womit}</td>
              <td className="px-4 py-3 text-slate-700">{z.wie}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErsteHilfe({ eintraege, t }) {
  if (!eintraege?.length) return null;
  return (
    <div
      className="border border-red-200 rounded-lg bg-red-50 p-4"
      data-testid="monteur-hsp-erste-hilfe"
    >
      <h2 className="font-heading text-base md:text-lg font-bold text-red-900">
        {t("hsp.firstAidTitle")}
      </h2>
      <ul className="mt-2 space-y-2">
        {eintraege.map((e, idx) => (
          <li key={idx} className="text-sm text-red-900 flex gap-2">
            <span className="font-semibold shrink-0">{e.produkt}:</span>
            <span>{e.massnahme}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Gefahrstoffverzeichnis · Pflichtangaben je Einsatzbereich (Aug 2026,
// § 6 Abs. 12 Satz 4 GefStoffV): Stoffbezeichnung, Einstufung/Gefahren,
// Mengenbereich, Einsatzbereich + PSA. Whitelist-gefiltert vom Server
// (routes/me_hazmat_verzeichnis.py) — hier reine Anzeige.
const GSV_PSA_LABEL_KEYS = {
  handschutz: "hsp.psaHandschutz",
  atemschutz: "hsp.psaAtemschutz",
  augenschutz: "hsp.psaAugenschutz",
  koerperschutz: "hsp.psaKoerperschutz",
  hautkontakt_erste_hilfe: "hsp.psaErsteHilfe",
  aetzwirkung_reizung: "hsp.psaAetzReiz",
  sensibilisierung: "hsp.psaSensibilisierung",
  p_saetze: "hsp.psaPSaetze",
};

function GefahrstoffListe({ eintraege, t }) {
  return (
    <ul className="space-y-4" data-testid="monteur-gsv-liste">
      {eintraege.map((stoff, i) => {
        const c = stoff.classification || {};
        const psaZeilen = Object.entries(GSV_PSA_LABEL_KEYS)
          .map(([key, labelKey]) => [t(labelKey), (stoff.psa || {})[key]])
          .filter(([, wert]) => !!wert);
        const ghsCodes = (c.ghs_piktogramme || "")
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean);
        return (
          <li
            key={`${stoff.product_name}-${i}`}
            className="border border-slate-200 rounded-md p-3"
            data-testid={`monteur-gsv-stoff-${i}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900 text-sm">
                {stoff.product_name}
              </span>
              {stoff.product_manufacturer && (
                <span className="text-xs text-slate-500">
                  {stoff.product_manufacturer}
                </span>
              )}
              {c.signalwort && (
                <span
                  className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2 py-0.5 ${
                    c.signalwort.trim().toLowerCase() === "gefahr"
                      ? "bg-red-100 text-red-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {c.signalwort}
                </span>
              )}
              {ghsCodes.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center rounded border border-slate-300 bg-slate-50 text-slate-700 text-[11px] font-mono px-1.5 py-0.5"
                >
                  {code}
                </span>
              ))}
            </div>
            {stoff.quantity_tier_label && (
              <div className="text-xs text-slate-600 mt-1">
                {t("hsp.gsvMenge")} {stoff.quantity_tier_label}
              </div>
            )}
            {c.h_saetze && (
              <div className="text-xs text-slate-600 mt-1">
                <span className="font-medium">{t("hsp.gsvHSaetze")}</span> {c.h_saetze}
              </div>
            )}
            {psaZeilen.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {psaZeilen.map(([label, wert]) => (
                  <div key={label} className="text-xs text-slate-600">
                    <span className="font-medium">{label}:</span> {wert}
                  </div>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PlakatAnsicht({ status, imageUrl, errorText, showLoadingHint, onError, t }) {
  if (status === "loading") {
    return (
      <div className="space-y-3">
        <div
          className="relative w-full bg-slate-100 border border-slate-200 rounded-lg overflow-hidden"
          style={{ aspectRatio: "1 / 1.414" }}
          data-testid="monteur-hsp-plakat-loading"
          aria-label={t("hsp.plakatLoadingAria")}
        >
          <div className="absolute inset-0 animate-pulse bg-slate-100" />
          {showLoadingHint && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"
              data-testid="monteur-hsp-plakat-hint"
            >
              <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
              <div className="max-w-xs">
                <p className="font-heading font-semibold text-slate-800">
                  {t("hsp.plakatCreatingTitle")}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {t("hsp.plakatCreatingText")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div
        className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-900"
        data-testid="monteur-hsp-plakat-error"
      >
        <p className="font-semibold">{t("hsp.plakatFailedTitle")}</p>
        <p className="mt-1">
          {errorText || t("hsp.plakatFailedHint")}
        </p>
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      alt={t("hsp.plakatAlt")}
      className="w-full h-auto border border-slate-200 rounded-lg bg-white shadow-sm"
      data-testid="monteur-hsp-plakat-img"
      onError={onError}
      draggable={false}
    />
  );
}

export default function MonteurHandschutzplan() {
  const navigate = useNavigate();
  const { einsatzbereichId } = useParams();
  const t = useMonteurT();
  const [plan, setPlan] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [plakatStatus, setPlakatStatus] = useState("loading");
  const [plakatUrl, setPlakatUrl] = useState("");
  const [plakatErrorText, setPlakatErrorText] = useState("");
  const [showLoadingHint, setShowLoadingHint] = useState(false);

  const [tabelleOffen, setTabelleOffen] = useState(false);

  // Gefahrstoffverzeichnis-Pflichtangaben (§ 6 Abs. 12 S. 4 GefStoffV).
  // Unabhängig vom Handschutzplan geladen — die Zugänglichkeit der
  // Angaben hängt nicht davon ab, ob bereits ein Plan generiert wurde.
  // Fehler werden still geschluckt (Zero-Noise: kein Abschnitt).
  const [gefahrstoffe, setGefahrstoffe] = useState([]);
  const [stoffeOffen, setStoffeOffen] = useState(false);

  const objectUrlRef = useRef("");
  const cancelledRef = useRef(false);

  // Content laden.
  useEffect(() => {
    let cancelled = false;
    api
      .getMyHandschutzplaene()
      .then((rows) => {
        if (cancelled) return;
        const found = (rows || []).find(
          (r) => r.einsatzbereich_id === einsatzbereichId,
        );
        setPlan(found || null);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403) setForbidden(true);
        else setErrorMsg(t("hsp.loadError"));
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [einsatzbereichId]);

  // Gefahrstoff-Pflichtangaben laden (Zero-Noise: Fehler ⇒ leere Liste).
  useEffect(() => {
    let cancelled = false;
    api
      .getMyGefahrstoffe(einsatzbereichId)
      .then((rows) => {
        if (!cancelled) setGefahrstoffe(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setGefahrstoffe([]);
      });
    return () => {
      cancelled = true;
    };
  }, [einsatzbereichId]);

  // Plakat-Zwei-Schritt-Flow: prepare → (optional) Poll → JPEG-Fetch.
  useEffect(() => {
    if (!loaded || forbidden || errorMsg || !plan) {
      return;
    }
    cancelledRef.current = false;
    setPlakatStatus("loading");
    setShowLoadingHint(false);
    setPlakatErrorText("");

    // Hint-Timer: „Plakat wird erstellt" erst nach kurzer Anlaufzeit.
    const hintTimer = window.setTimeout(() => {
      if (!cancelledRef.current) setShowLoadingHint(true);
    }, LOADING_HINT_DELAY_MS);

    const clearHint = () => {
      window.clearTimeout(hintTimer);
      if (!cancelledRef.current) setShowLoadingHint(false);
    };

    const loadJpeg = async () => {
      const { status: httpStatus, blob } = await api.fetchMyHandschutzplanPlakatJpeg(
        einsatzbereichId,
      );
      if (cancelledRef.current) return;
      if (httpStatus === 200 && blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPlakatUrl(url);
        setPlakatStatus("ready");
      } else {
        // 409 bedeutet Server hat parallel prepare-Zustand verloren
        // (Race). Fallback: Fehler zeigen.
        setPlakatStatus("error");
        setPlakatErrorText(t("hsp.plakatReload"));
        setTabelleOffen(true);
      }
    };

    const pollUntilReady = async () => {
      const start = Date.now();
      while (!cancelledRef.current) {
        if (Date.now() - start > POLL_HARD_TIMEOUT_MS) {
          setPlakatStatus("error");
          setPlakatErrorText(t("hsp.plakatTimeout"));
          setTabelleOffen(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (cancelledRef.current) return;
        let statusResp;
        try {
          statusResp = await api.getMyPlakatStatus(einsatzbereichId);
        } catch (_) {
          // Netzwerkfehler beim Poll — kein sofortiger Abbruch, nochmal
          // versuchen. Ingress-Retries sind billig.
          continue;
        }
        if (cancelledRef.current) return;
        if (statusResp.status === "ready") {
          await loadJpeg();
          return;
        }
        if (statusResp.status === "error") {
          setPlakatStatus("error");
          setPlakatErrorText(
            statusResp.error || t("hsp.plakatRetry"),
          );
          setTabelleOffen(true);
          return;
        }
        // Sonst weiter pollen (idle/generating)
      }
    };

    (async () => {
      try {
        const prep = await api.prepareMyPlakat(einsatzbereichId);
        if (cancelledRef.current) return;
        if (prep.status === "ready") {
          // Reuse-Kurzschluss: direkt JPEG holen.
          await loadJpeg();
        } else if (prep.status === "error") {
          setPlakatStatus("error");
          setPlakatErrorText(
            prep.error || t("hsp.plakatRetry"),
          );
          setTabelleOffen(true);
        } else {
          // generating → pollen.
          await pollUntilReady();
        }
      } catch (err) {
        if (cancelledRef.current) return;
        setPlakatStatus("error");
        setPlakatErrorText(t("hsp.plakatLoadFailed"));
        setTabelleOffen(true);
      } finally {
        clearHint();
      }
    })();

    return () => {
      cancelledRef.current = true;
      window.clearTimeout(hintTimer);
    };
  }, [einsatzbereichId, loaded, forbidden, errorMsg, plan]);

  // Object-URL beim Unmount freigeben.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }
    };
  }, []);

  const content = plan?.content;
  const titel =
    content?.titel ||
    `${t("hsp.titleFallback")}${plan?.einsatzbereich_name ? ` — ${plan.einsatzbereich_name}` : ""}`;

  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid="monteur-handschutzplan-page"
    >
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/monteur")}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            data-testid="monteur-hsp-back"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </button>
          <div className="flex-1" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            {t("hsp.kicker")}
            {plan?.einsatzbereich_name ? ` · ${plan.einsatzbereich_name}` : ""}
          </p>
          <h1
            className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1"
            data-testid="monteur-hsp-titel"
          >
            {titel}
          </h1>
        </div>

        {forbidden ? (
          <div
            className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm"
            data-testid="monteur-hsp-forbidden"
          >
            {t("common.notEnabled")}
          </div>
        ) : errorMsg ? (
          <div
            className="border border-red-200 bg-red-50 text-red-900 rounded-md p-4 text-sm"
            data-testid="monteur-hsp-error"
          >
            {errorMsg}
          </div>
        ) : !loaded ? (
          <div
            className="text-slate-500 text-sm"
            data-testid="monteur-hsp-loading"
          >
            {t("common.loading")}
          </div>
        ) : !plan ? (
          <div
            className="border border-slate-200 bg-white rounded-lg p-8 text-center"
            data-testid="monteur-hsp-empty"
          >
            <p className="text-slate-600 text-sm">
              {t("hsp.empty")}
            </p>
          </div>
        ) : (
          <>
            <PlakatAnsicht
              status={plakatStatus}
              imageUrl={plakatUrl}
              errorText={plakatErrorText}
              showLoadingHint={showLoadingHint}
              t={t}
              onError={() => {
                setPlakatStatus("error");
                setPlakatErrorText(t("hsp.plakatImgError"));
                setTabelleOffen(true);
              }}
            />

            <div
              className="border border-slate-200 rounded-lg bg-white overflow-hidden"
              data-testid="monteur-hsp-textansicht"
            >
              <button
                type="button"
                onClick={() => setTabelleOffen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors"
                data-testid="monteur-hsp-textansicht-toggle"
                aria-expanded={tabelleOffen}
                aria-controls="monteur-hsp-textansicht-content"
              >
                <span>
                  {tabelleOffen ? t("hsp.textClose") : t("hsp.textOpen")}
                </span>
                {tabelleOffen ? (
                  <ChevronUp className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                )}
              </button>
              {tabelleOffen && (
                <div
                  id="monteur-hsp-textansicht-content"
                  className="border-t border-slate-100 p-4 space-y-4"
                  data-testid="monteur-hsp-textansicht-content"
                >
                  {content?.zeilen?.length > 0 && (
                    <PlanTabelle zeilen={content.zeilen} t={t} />
                  )}
                  <ErsteHilfe eintraege={content?.erste_hilfe_hautkontakt} t={t} />
                </div>
              )}
            </div>
          </>
        )}

        {/* Gefahrstoff-Pflichtangaben (§ 6 Abs. 12 S. 4 GefStoffV) —
            erscheint auch ohne generierten Handschutzplan, aber nur,
            wenn diesem Bereich Gefahrstoffe zugeordnet sind (Zero-Noise). */}
        {!forbidden && !errorMsg && loaded && gefahrstoffe.length > 0 && (
          <div
            className="border border-slate-200 rounded-lg bg-white overflow-hidden"
            data-testid="monteur-gsv-abschnitt"
          >
            <button
              type="button"
              onClick={() => setStoffeOffen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors"
              data-testid="monteur-gsv-toggle"
              aria-expanded={stoffeOffen}
              aria-controls="monteur-gsv-content"
            >
              <span>
                {gefahrstoffe.length === 1
                  ? t("hsp.gsvOne")
                  : t("hsp.gsvMany", { count: gefahrstoffe.length })}
              </span>
              {stoffeOffen ? (
                <ChevronUp className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              )}
            </button>
            {stoffeOffen && (
              <div
                id="monteur-gsv-content"
                className="border-t border-slate-100 p-4"
                data-testid="monteur-gsv-content"
              >
                <GefahrstoffListe eintraege={gefahrstoffe} t={t} />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
